import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLComponentTypeManifest } from '../interfaces';
import {
  TpETLPipelineComponent,
  TpETLDryRun,
  TpETLDryRunComponentResult,
  TpETLDryRunMatrixObject,
  isDryRunMatrixObject,
} from '../../interfaces';
import { getETLComponentInputObject, addDryRunObject } from '../../utils';
import { addETLDryRunError } from '../../etlDryRun';
import { VALTYPES } from '../../../../data/interfaces';
import { createConfigParamTypeMultipleChoice } from '../utils';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  // Creates a matrix with observations being either the original observation or feature metadata,
  // depending on the dimension selected in the component parameters.
  // The new features are the principal components generated by PCA, and the metadata is related to the PCA results.
  const matrix: TpETLDryRunMatrixObject = {
    type: TpETLObjectTypes.MATRIX,
    id: comp.outputObjectIds['result'],
    createdByCompId: comp.id,
    obsMetadataColumns: [],
    featureMetadataColumns: [
      {
        identifier: 'component_id',
        name: 'Principal Component ID',
        description: 'Name of the principal component',
        dataType: VALTYPES.STRING,
      },
      {
        identifier: 'singular_value',
        name: 'Singular Value',
        description: 'Singular value of the principal component.',
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: 'explained_variance',
        name: 'Explained Variance',
        description: 'The amount of variance explained by the principal component.',
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: 'explained_variance_ratio',
        name: 'Explained Variance Ratio',
        description: 'Percentage of variance explained by each of the selected components.',
        dataType: VALTYPES.NUMBER,
      },
      {
        identifier: 'cumulative_explained_var',
        name: 'Cumulative Explained Variance Ratio',
        description: 'Cumulative percentage of variance explained by the selected components.',
        dataType: VALTYPES.NUMBER,
      },
    ],
    linkColumnId: 'component_id',
  };
  addDryRunObject(dryRun, matrix);

  // Add the observation metadata columns from the input matrix
  const input = getETLComponentInputObject(dryRun, comp, 'source');
  if (!isDryRunMatrixObject(input)) {
    addETLDryRunError(dryRun, `Input is not a matrix`, comp.id, 'source');
    return { abort: false };
  }

  const dimension = comp.params['dimension'];
  // Normal transformation (features) keeps the observation metadata.
  // Transposing results in the feature metadata being used as observation metadata.
  const observationMetadata = dimension === 'observations' ? input.featureMetadataColumns : input.obsMetadataColumns;
  matrix.obsMetadataColumns = observationMetadata.map((col) => structuredClone(col));

  return {
    abort: false,
  };
}

export const manifestETLApplyPcaMatrix: TpETLComponentTypeManifest = {
  id: 'ApplyPcaMatrix',
  name: 'Principal Component Analysis',
  description: 'Perform principal component analysis on a normalized matrix.',
  categoryId: 'matrix',
  icon: <FontAwesomeIcon icon={['fas', 'hexagon-nodes']} />,
  performDryRun,
  inputs: [
    {
      id: 'source',
      name: 'Source matrix',
      type: TpETLObjectTypes.MATRIX,
      description: 'Matrix containing normalized feature observations.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  outputs: [
    {
      type: TpETLObjectTypes.MATRIX,
      id: 'result',
      name: 'PC Matrix',
      description: 'Matrix with principal components.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  params: [
    {
      id: 'dimension',
      name: 'Matrix Dimension',
      description:
        'Dimension to transform. Selecting observations produces components for observations by transposing the data. ' +
        'Selecting features performs standard PCA and generates components for the features.',
      paramType: createConfigParamTypeMultipleChoice(
        [
          { id: 'features', name: 'Features' },
          { id: 'observations', name: 'Observations (transpose)' },
        ],
        'observations', // Default to observations
      ),
    },
  ],
};
